import type { Scope } from '@/app/core/scope/scope.service';
import type { CurrentUserService, GlobalPermissionSpec, TenantRequirement } from './current-user.service';
import { APPLICATION_LEASES_SPECS, TENANT_CONTROLS, TENANTS_LIST_GLOBAL_SPECS } from './tenant-controls';

/**
 * The GLOBAL API-client flags: the server's `QUERY_TENANT_APPLICATION_LEASE_API_CLIENTS` admits any of them in every tenant
 * (`TENANT_CONTROLS.viewApiClients` lists only VIEW — its operator gate — and must not decide a read across tenants, the
 * pregled 3 lesson of `APPLICATION_LEASES_SPECS`).
 */
export const API_CLIENT_GLOBAL_SPECS = ['global.tenants:VIEW_API_CLIENTS', 'global.tenants:ADD_API_CLIENTS', 'global.tenants:EDIT_API_CLIENTS', 'global.tenants:DELETE_API_CLIENTS'] as const satisfies readonly GlobalPermissionSpec[];

/**
 * What the hub tab „API klijenti“ needs in a tenant — the hub itself and the tab's own requirements (`hubTabRequirements('api-klijenti')`
 * in `features/tenant-hub/tenant-hub-tabs.ts`; a spec keeps the two equal, core does not import a feature).
 */
export const API_CLIENTS_HUB_TAB: readonly TenantRequirement[] = [TENANT_CONTROLS.hub, TENANT_CONTROLS.viewApiClients, TENANT_CONTROLS.viewLeases];

/** The actor as the scope reads it — `CurrentUserService` in the app, any stand-in in a spec. */
export type ApiClientRegistryActor = Pick<CurrentUserService, 'hasAny' | 'canAllInTenant'>;

/**
 * Where /api-klijenti takes the actor (UX pregled 4, D-UX4-1):
 * - `registry` — the whole list across tenants, `withRules` = the rules column (`viewApiClientRules.global`; without it the
 *   column is withheld, never „Bez ograničenja“);
 * - `redirect` — a scope bounded to ONE tenant whose hub tab „API klijenti“ opens for the actor: that tab, without a history
 *   entry (the `/lease` rule);
 * - `forbidden` — anyone else: `/zabranjen-pristup`.
 */
export type ApiClientRegistryScope = { readonly kind: 'registry'; readonly withRules: boolean } | { readonly kind: 'redirect'; readonly tenantId: string } | { readonly kind: 'forbidden' };

/**
 * Whether the registry is read at all: there is no root cross-tenant API-client query (N8), so it walks `tenants →
 * tenancyApplicationLeases → applicationLeaseApiClients`, and every level is guarded per tenant. An operator reads every tenant's
 * level only with a GLOBAL flag of each: the root `tenants` list whole (`TENANTS_LIST_GLOBAL_SPECS`, else it is narrowed to the
 * tenants whose information the actor reads — refix F4), the leases (`APPLICATION_LEASES_SPECS`) and the API clients
 * (`API_CLIENT_GLOBAL_SPECS`). The menu entry and the route read this one predicate.
 */
export function apiClientRegistryAvailable(actor: Pick<CurrentUserService, 'hasAny'>): boolean {
    return actor.hasAny(API_CLIENT_GLOBAL_SPECS) && actor.hasAny(APPLICATION_LEASES_SPECS) && actor.hasAny(TENANTS_LIST_GLOBAL_SPECS);
}

/**
 * Where /api-klijenti takes the actor (D-UX4-1): the registry where it is available; else, for a scope bounded to exactly one
 * tenant, that tenant's hub tab where it opens (the caller has `ensureTenantCapability(tenantId)` first — an unknown capability
 * reads „no“); else forbidden. Decided up front from the flags, never from a refused read.
 */
export function apiClientRegistryScope(actor: ApiClientRegistryActor, scope: Scope): ApiClientRegistryScope {
    if (apiClientRegistryAvailable(actor)) return { kind: 'registry', withRules: actor.hasAny(TENANT_CONTROLS.viewApiClientRules.global) };
    const single = scope.kind === 'bounded' && scope.tenantIds.length === 1 ? scope.tenantIds[0] : null;
    if (single && actor.canAllInTenant(single, API_CLIENTS_HUB_TAB)) return { kind: 'redirect', tenantId: single };
    return { kind: 'forbidden' };
}
